package gr.tech.pdfgenerator.infrastructure.api

import gr.tech.GRAttestation.models.api.ApiResponse
import gr.tech.pdfgenerator.models.pdf.PdfDTO
import gr.tech.pdfgenerator.models.projects.ProjectsDTO
import gr.tech.pdfgenerator.util.JsonWithDefault
import gr.tech.pdfgenerator.util.BACKEND
import kotlinx.serialization.encodeToString
import org.w3c.files.Blob
import org.w3c.xhr.FormData

// This function sends current project, and gets the returned/updated
// Project BACK. This will allow the last save timer to display most accurate time.
suspend fun projectSaveToDatabase(
    id: Int?,
    project: ProjectsDTO
): ApiResponse<ProjectsDTO> {
    val url = BACKEND.BACKEND_URL + if (id != null) BACKEND.ROUTES.PROJECTS_UPDATE else BACKEND.ROUTES.PROJECTS_SAVE
    return safeApiCallStringBody(
        url = url,
        method = "POST",
        body = JsonWithDefault.encodeToString(project)
    )
}

suspend fun fetchProjects(
    skip: Int,
    onSuccess: (List<ProjectsDTO>) -> Unit,
    onError: (String) -> Unit
) {
    val response = safeApiCallStringBody<List<ProjectsDTO>>(
        url = "${BACKEND.BACKEND_URL}${BACKEND.ROUTES.PROJECTS}?skip=$skip"
    )
    if (response.data != null) {
        onSuccess(response.data)
    } else {
        onError("Error fetching projects!")
    }
}

suspend fun fetchProjectByID(
    id: String,
    onApiResponse: (ProjectsDTO) -> Unit,
    onError: (String) -> Unit
) {
    val response = safeApiCallStringBody<ProjectsDTO>(
        url = "${BACKEND.BACKEND_URL}${BACKEND.ROUTES.PROJECTS}/$id"
    )
    if (response.success && response.data != null) {
        onApiResponse(response.data)
    } else if (response.error != null) {
        onError(response.error.code)
    } else {
        onError("db_error")
    }
}

suspend fun deleteProjectByID(
    id: Int,
    onSuccess: () -> Unit,
    onError: (String) -> Unit
) {
    val response = safeApiCallStringBody<Unit>(
        url = "${BACKEND.BACKEND_URL}${BACKEND.ROUTES.PROJECTS_DELETE}/$id"
    )
    if (response.success) {
        onSuccess()
    } else {
        onError("Error deleting project!")
    }
}

suspend fun duplicateProjectByID(
    id: Int,
    onSuccess: () -> Unit,
    onError: (String) -> Unit
) {
    val response = safeApiCallStringBody<Unit>(
        url = "${BACKEND.BACKEND_URL}${BACKEND.ROUTES.PROJECTS_DUPE}/$id",
        method = "POST"
    )
    if (response.success) {
        onSuccess()
    } else {
        onError("Error duplicating project!")
    }
}

/*
        PROJECT PDF FUNCTIONS
 */
suspend fun savePDFWithProjectID(
    projectId: Int,
    projectName: String,
    projectType: Int,
    pdfBlob: Blob
): ApiResponse<Unit> {
    val formData = FormData()
    formData.append("projectId", projectId.toString())
    formData.append("projectType", projectType.toString())
    formData.append("pdf", pdfBlob, projectName)
    return safeApiCallFormData(
        url = "${BACKEND.BACKEND_URL}${BACKEND.ROUTES.PROJECT_PDF_SAVE}",
        formData = formData
    )
}

suspend fun fetchPDFsByID(
    id: Int,
    skip: Int,
    onSuccess: (List<PdfDTO>) -> Unit,
    onError: (String) -> Unit
) {
    val response = safeApiCallStringBody<List<PdfDTO>>(
        url = "${BACKEND.BACKEND_URL}${BACKEND.ROUTES.PROJECT_PDF}/$id?skip=$skip"
    )
    if (response.data != null) {
        onSuccess(response.data)
    } else {
        onError("Error fetching project PDFs!")
    }
}

suspend fun deletePDFById(
    id: Int,
    projectId: Int,
    onSuccess: () -> Unit,
    onError: (String) -> Unit
) {
    val response = safeApiCallStringBody<Unit>(
        url = "${BACKEND.BACKEND_URL}${BACKEND.ROUTES.PROJECT_PDF_DELETE}/$projectId/$id",
        method = "POST"
    )
    if (response.success) {
        onSuccess()
    } else {
        onError("Error deleting PDF!")
    }
}

/*
        HELPER FUNCTIONS
 */
fun parseProjectApiErrorResponse(code: String): String {
    return when (code) {
        "no_project" -> { "No project found." }
        else -> { "Error! Please try again later." }
    }
}
